/* ===== Input Validation Utilities ===== */

/** Fișier original de pe dispozitiv — compresia client reduce înainte de upload. */
export const MAX_ORIGINAL_IMAGE_BYTES = 40 * 1024 * 1024;
export const MAX_STANDARD_FILE_BYTES = 10 * 1024 * 1024;
export const COMPRESSIBLE_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

// Validate email format
export function validateEmail(email) {
  const s = String(email || '').trim();
  return /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z]{2,}$/i.test(s);
}

// Validate Romanian CUI (CUI format: RO12345678 or just 12345678)
export function validateCUI(cui) {
  const s = String(cui || '').trim().toUpperCase().replace(/^RO/, '');
  // CUI should be 2-10 digits
  return /^\d{2,10}$/.test(s);
}

// Telefon: românesc (mobil 07…, fix 02…/03…, cu sau fără +40 / 0040) sau internațional (cu + ori 00 în față, 10–15 cifre).
// Înainte, orice șir cu cel puțin 6 cifre trecea („123456”), iar comerciantul nu-l putea suna pe câștigător.
export function validatePhone(phone) {
  const s = String(phone || '').trim().replace(/[\s().-]/g, '');
  const digits = s.replace(/\D/g, '');
  if (!digits) return false;
  const ro = digits.replace(/^0040/, '0').replace(/^40(?=\d{9}$)/, '0');
  if (/^0[237]\d{8}$/.test(ro) || /^7\d{8}$/.test(ro)) return true; // „722…” = mobil fără 0 în față
  return /^(\+|00)[1-9]\d{9,14}$/.test(s);
}

// Validate password strength
export function validatePassword(password) {
  const s = String(password || '');
  if (s.length < 8) return { valid: false, message: 'Parola trebuie să aibă minim 8 caractere' };
  // Optional: add more checks
  // if (!/[A-Z]/.test(s)) return { valid: false, message: 'Parola trebuie să conțină cel puțin o literă mare' };
  // if (!/[0-9]/.test(s)) return { valid: false, message: 'Parola trebuie să conțină cel puțin o cifră' };
  return { valid: true };
}

// Validate non-negative integer with optional max
export function validateNonNegativeInteger(value, max = null, fieldName = 'Valoarea') {
  const num = parseInt(String(value || ''), 10);
  if (isNaN(num)) {
    return { valid: false, message: `${fieldName} trebuie să fie un număr valid.` };
  }
  if (num < 0) {
    return { valid: false, message: `${fieldName} nu poate fi negativ.` };
  }
  if (max !== null && num > max) {
    return { valid: false, message: `${fieldName} nu poate fi mai mare decât ${max}.` };
  }
  return { valid: true, value: num };
}

// Validate integer in range
export function validateIntegerRange(value, min, max, fieldName = 'Valoarea') {
  const num = parseInt(String(value || ''), 10);
  if (isNaN(num)) {
    return { valid: false, message: `${fieldName} trebuie să fie un număr valid.` };
  }
  if (num < min || num > max) {
    return { valid: false, message: `${fieldName} trebuie să fie între ${min} și ${max}.` };
  }
  return { valid: true, value: num };
}

// Validate hex color format (#RRGGBB or #RGB)
export function validateHexColor(color) {
  const s = String(color || '').trim();
  if (!s) {
    return { valid: false, message: 'Culoarea este obligatorie.' };
  }
  if (!/^#[0-9A-Fa-f]{3}([0-9A-Fa-f]{3})?$/.test(s)) {
    return { valid: false, message: 'Culoarea trebuie să fie în format hex (#RRGGBB sau #RGB).' };
  }
  return { valid: true, value: s };
}

// Validate date format (YYYY-MM-DD)
export function validateDate(dateString, fieldName = 'Data') {
  const s = String(dateString || '').trim();
  if (!s) {
    return { valid: false, message: `${fieldName} este obligatorie.` };
  }
  // Check format YYYY-MM-DD
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return { valid: false, message: `${fieldName} trebuie să fie în format YYYY-MM-DD.` };
  }
  // Check if date is valid
  const date = new Date(s + 'T00:00:00');
  if (isNaN(date.getTime())) {
    return { valid: false, message: `${fieldName} nu este o dată validă.` };
  }
  // Check if date string matches parsed date (catches invalid dates like 2024-13-45)
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  if (`${year}-${month}-${day}` !== s) {
    return { valid: false, message: `${fieldName} nu este o dată validă.` };
  }
  return { valid: true, value: s, date };
}

// Validate date range (start date must be before end date)
export function validateDateRange(startDate, endDate) {
  const startValidation = validateDate(startDate, 'Data de început');
  if (!startValidation.valid) {
    return startValidation;
  }
  
  const endValidation = validateDate(endDate, 'Data de sfârșit');
  if (!endValidation.valid) {
    return endValidation;
  }
  
  if (startValidation.date >= endValidation.date) {
    return { valid: false, message: 'Data de început trebuie să fie înainte de data de sfârșit.' };
  }
  
  return { valid: true, startDate: startValidation.value, endDate: endValidation.value };
}

const HEIC_TYPES = new Set(['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence']);
const EXT_TO_MIME = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  heic: 'image/heic',
  heif: 'image/heif'
};

function resolveFileMime(file) {
  const raw = String(file?.type || '').trim().toLowerCase();
  if (raw) return raw;
  const ext = String(file?.name || '').split('.').pop()?.toLowerCase() || '';
  return EXT_TO_MIME[ext] || '';
}

// Validate file upload (size, type, count)
export function validateFileUpload(file, options = {}) {
  const {
    maxSize = 10 * 1024 * 1024, // 10MB default
    allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    fieldName = 'Fișierul'
  } = options;
  
  if (!file) {
    return { valid: false, message: `${fieldName} este obligatoriu.` };
  }

  const mime = resolveFileMime(file);
  if (HEIC_TYPES.has(mime)) {
    return {
      valid: false,
      message: `${fieldName}: format HEIC/HEIF nu este suportat. Folosește JPG sau PNG.`
    };
  }

  // Check file type (Android/iOS pot trimite type gol — extensia e deja în resolveFileMime)
  if (allowedTypes.length > 0 && mime && !allowedTypes.includes(mime)) {
    const typesList = allowedTypes.map(t => t.split('/')[1].toUpperCase()).join(', ');
    return { valid: false, message: `${fieldName} trebuie să fie de tip: ${typesList}.` };
  }
  // MIME gol: permis pe mobil (galerie/cameră); upload-ul validează mărimea
  
  // Check file size
  if (file.size > maxSize) {
    const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(1);
    return { valid: false, message: `${fieldName} este prea mare (max ${maxSizeMB}MB).` };
  }
  
  return { valid: true };
}

/** Imagini comprimate la upload — permitem original mare, respingem doar extrem de mari. */
export function validateCompressibleImageUpload(file, options = {}) {
  const {
    fieldName = 'Imaginea',
    maxSize = MAX_ORIGINAL_IMAGE_BYTES,
    allowedTypes = COMPRESSIBLE_IMAGE_TYPES
  } = options;
  return validateFileUpload(file, { maxSize, allowedTypes, fieldName });
}

// Validate multiple file uploads
export function validateFileUploads(files, options = {}) {
  const {
    maxCount = 5,
    maxSize = 10 * 1024 * 1024,
    allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    fieldName = 'Fișierele'
  } = options;
  
  if (!files || files.length === 0) {
    return { valid: true, files: [] }; // Empty is valid
  }
  
  if (files.length > maxCount) {
    return { valid: false, message: `Poți încărca maxim ${maxCount} ${fieldName.toLowerCase()}.` };
  }
  
  // Validate each file
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const validation = validateFileUpload(file, { maxSize, allowedTypes, fieldName: `${fieldName} #${i + 1}` });
    if (!validation.valid) {
      return validation;
    }
  }
  
  return { valid: true, files: Array.from(files) };
}

// Validate company name (non-empty, reasonable length)
/** URL extern opțional: gol permis; dacă e setat, doar http(s). */
export function validateMenuUrl(raw) {
  const s = (raw || '').trim();
  if (!s) return { valid: true, value: '' };
  if (s.length > 2048) return { valid: false, message: 'Link-ul este prea lung.' };
  try {
    const u = new URL(s);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') {
      return { valid: false, message: 'Link-ul trebuie să înceapă cu http:// sau https://' };
    }
    return { valid: true, value: s };
  } catch {
    return { valid: false, message: 'Link invalid.' };
  }
}

export function validateCompanyName(name) {
  const s = String(name || '').trim();
  if (!s) {
    return { valid: false, message: 'Numele firmei este obligatoriu.' };
  }
  if (s.length < 2) {
    return { valid: false, message: 'Numele firmei trebuie să aibă minim 2 caractere.' };
  }
  if (s.length > 100) {
    return { valid: false, message: 'Numele firmei nu poate depăși 100 de caractere.' };
  }
  return { valid: true, value: s };
}

// Sanitize input (enhanced XSS prevention)
export function sanitizeInput(input) {
  if (typeof input !== 'string') return '';
  
  return input
    .trim()
    // Remove dangerous HTML tags and scripts
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
    .replace(/<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi, '')
    .replace(/<embed\b[^<]*(?:(?!<\/embed>)<[^<]*)*<\/embed>/gi, '')
    // Remove javascript: and data: URLs
    .replace(/javascript:/gi, '')
    .replace(/data:text\/html/gi, '')
    .replace(/on\w+\s*=/gi, '') // Remove event handlers (onclick, onerror, etc.)
    // Escape remaining HTML characters
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;')
    // Limit length
    .slice(0, 500);
}

export const DEFAULT_RESERVATION_MESSAGE =
  'Pentru rezervări, te rugăm să ne apelezi la numărul afișat!';

/** Mesaj afișat clientului (personalizat sau standard). */
export function getReservationClientMessage(settings) {
  const custom = String(settings?.reservationMessage || '').trim();
  return custom || DEFAULT_RESERVATION_MESSAGE;
}

/** Cifre pentru href tel: (fără spații). */
export function normalizeTelHref(phone) {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return '';
  return `tel:${digits}`;
}

/** Link Google Maps (share, maps.app.goo.gl, maps.google.com). */
export function validateGoogleMapsUrl(raw) {
  const s = String(raw || '').trim();
  if (!s) {
    return { valid: false, message: 'Introdu linkul Google Maps.' };
  }
  if (s.length > 2048) {
    return { valid: false, message: 'Linkul Google Maps este prea lung.' };
  }
  let u;
  try {
    u = new URL(s);
  } catch {
    return { valid: false, message: 'Link Google Maps invalid.' };
  }
  if (u.protocol !== 'http:' && u.protocol !== 'https:') {
    return { valid: false, message: 'Linkul trebuie să înceapă cu http:// sau https://' };
  }
  const host = u.hostname.toLowerCase().replace(/^www\./, '');
  const pathQuery = `${u.pathname}${u.search}`;
  const isMaps =
    host === 'maps.app.goo.gl' ||
    host === 'maps.google.com' ||
    host === 'share.google' ||
    host === 'share.google.com' ||
    (host === 'google.com' && /\/maps|\/maps\//i.test(pathQuery)) ||
    (host.endsWith('.google.com') && /\/maps|\/maps\//i.test(pathQuery)) ||
    (host === 'goo.gl' && /maps/i.test(pathQuery)) ||
    (host === 'g.co' && pathQuery.length > 1);
  if (!isMaps) {
    return {
      valid: false,
      message:
        'Folosește un link Google Maps (share.google, maps.google.com, google.com/maps sau maps.app.goo.gl).'
    };
  }
  return { valid: true, value: s };
}

export function validateReservationPhoneInput(phone) {
  const raw = String(phone || '').trim();
  if (!raw) {
    return { valid: false, message: 'Introdu numărul de telefon.' };
  }
  const digits = raw.replace(/\D/g, '');
  if (digits.length < 6 || digits.length > 15) {
    return { valid: false, message: 'Număr de telefon invalid (minim 6 cifre).' };
  }
  if (!validatePhone(raw)) {
    return { valid: false, message: 'Număr de telefon invalid.' };
  }
  return { valid: true, value: raw.slice(0, 32) };
}
